/**
 * Transits screen — the user's real 7-day transit feed, grouped by day.
 *
 * Was a hardcoded five-row illustration ("Меркурий тригон Сатурн · Сегодня
 * 12:47") shown identically to everyone, with a frozen "11–17 мая" header.
 * Now every row comes from `POST /transits/week` computed against the user's
 * own natal chart (see `fetchTransitsWeek`).
 *
 * Timing honesty: the engine samples once per day, so a row states the orb and
 * whether the aspect is closing or separating — not a clock time we would have
 * had to invent. Days with nothing inside the working orb say so.
 */
import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";

import { TabBar } from "@/components/TabBar";
import { TransitRow } from "@/components/TransitRow";
import { fetchTransitsWeek, type TransitDay, type TransitsWeekResponse } from "@/lib/api";
import { useTgContext } from "@/state/tg";

export default function TransitsPage(): ReactElement {
  const tg = useTgContext();

  const { data, isLoading, error } = useQuery<TransitsWeekResponse>({
    queryKey: ["transits-week", tg.initData ?? "guest"],
    queryFn: () => fetchTransitsWeek(tg.initData),
    staleTime: 30 * 60_000,
  });

  return (
    <main className="screen starry">
      <div className="body-pad has-tabbar">
        <header className="today-head">
          <div className="today-greet">
            <div className="day">{data?.rangeLabel ? `Неделя · ${data.rangeLabel}` : "Неделя"}</div>
            <div className="name">Транзиты</div>
          </div>
        </header>

        {isLoading ? <SkeletonFeed /> : null}

        {error ? (
          <div className="ob-error" role="alert">
            Не удалось загрузить транзиты: {(error as Error).message}
          </div>
        ) : null}

        {data && !data.onboarded ? <NotOnboarded /> : null}

        {data?.onboarded ? data.days.map((day) => <DaySection key={day.date} day={day} />) : null}
      </div>
      <TabBar active="transits" />
    </main>
  );
}

function DaySection({ day }: { readonly day: TransitDay }): ReactElement {
  return (
    <>
      <h2 className="section-h">
        <span>{day.label}</span>
      </h2>
      {day.transits.length === 0 ? (
        <p
          style={{
            margin: "0 0 14px",
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--fg-tertiary)",
          }}
        >
          Значимых аспектов в пределах рабочего орба нет — спокойный фон.
        </p>
      ) : (
        day.transits.map((t) => (
          <TransitRow key={t.id} glyph={t.icon} title={t.title} time={t.time} aspect={t.aspect} />
        ))
      )}
    </>
  );
}

function SkeletonFeed(): ReactElement {
  return (
    <div role="status" aria-busy="true" aria-label="Загрузка транзитов" style={{ opacity: 0.4 }}>
      <div className="moon-card" style={{ minHeight: 64, marginBottom: 10 }} />
      <div className="moon-card" style={{ minHeight: 64, marginBottom: 10 }} />
      <div className="moon-card" style={{ minHeight: 64 }} />
    </div>
  );
}

/** Session is valid but there is no birth data — same dignified prompt as Today. */
function NotOnboarded(): ReactElement {
  return (
    <article className="moon-card" role="note">
      <h3 style={{ margin: "0 0 8px" }}>Заполните данные рождения</h3>
      <p style={{ margin: 0 }}>
        Транзиты считаются относительно вашей натальной карты. Добавьте дату, время и место рождения
        — без них показывать нечего.
      </p>
    </article>
  );
}
